<template>
    <div :class="[rootClasses, customClass]">
        <ValidationProvider
            :name="name || label"
            :vid="vid"
            :rules="computedRules"
            :value="value"
            v-slot="{ errors }"
            mode="eager"
        >
            <div
                class="tw:relative tw:group"
                :data-validation-error="errors[0] ? '' : undefined"
            >
                <!-- Prefix Slot -->
                <div
                    v-if="$slots.prefix"
                    class="tw:absolute tw:left-3 tw:top-1/2 tw:-translate-y-1/2 tw:flex tw:items-center tw:text-slate-400 tw:transition-colors group-focus-within:tw:text-blue-500"
                >
                    <slot name="prefix"></slot>
                </div>

                <!-- Input -->
                <input
                    :id="id"
                    ref="inputRef"
                    :type="(allowDecimal || decimalsOnly) ? 'text' : 'number'"
                    :name="name || undefined"
                    :value="value"
                    :min="min"
                    :max="max"
                    :step="allowDecimal ? 'any' : step"
                    :inputmode="(allowDecimal || decimalsOnly) ? 'decimal' : undefined"
                    :disabled="disabled"
                    :readonly="readonly"
                    :placeholder="placeholder"
                    class="tw:block tw:w-full tw:text-sm tw:transition-all tw:duration-200 focus:tw:outline-none tw:peer"
                    :class="[
                        (errors[0] || error)
                            ? 'tw:border tw:border-red-500 tw:bg-red-50/50 focus:tw:border-blue-500 tw:p-2 tw:rounded'
                            : variantClasses,
                        { 'tw:pl-10': $slots.prefix },
                        { 'tw:pr-10': $slots.suffix || showStepper },
                        { 'tw:pr-20': $slots.suffix && showStepper },
                        { 'tw:opacity-60 tw:cursor-not-allowed': disabled }
                    ]"
                    v-bind="$attrs"
                    @input="onInput"
                    @keydown="onKeydown"
                    @blur="$emit('blur', $event)"
                    @focus="$emit('focus', $event)"
                />

                <!-- Floating Label -->
                <span
                    v-if="label"
                    class="tw:absolute tw:uppercase tw:duration-300 tw:z-10 tw:pointer-events-none tw:tracking-wide tw:origin-left tw:bg-white tw:px-2 tw:whitespace-nowrap
                    tw:start-1 tw:top-2 tw:-translate-y-4 tw:scale-75
                    tw:peer-placeholder-shown:max-w-[calc(100%-0.7rem)] tw:peer-placeholder-shown:overflow-hidden
                    tw:peer-placeholder-shown:scale-100 tw:peer-placeholder-shown:-translate-y-1/2 tw:peer-placeholder-shown:top-1/2 tw:peer-placeholder-shown:font-normal
                    tw:peer-focus:max-w-none tw:peer-focus:overflow-visible
                    tw:peer-focus:top-2 tw:peer-focus:-translate-y-4 tw:peer-focus:scale-75 tw:peer-focus:px-2"
                    :class="[
                        (errors[0] || error)
                            ? 'tw:text-red-400 tw:peer-placeholder-shown:bg-transparent tw:peer-focus:bg-white tw:peer-focus:text-blue-800 tw:peer-focus:font-bold'
                            : 'tw:text-slate-600 tw:font-bold tw:peer-focus:text-blue-800 tw:peer-focus:font-bold'
                    ]"
                >
                    {{ label }}
                </span>

                <!-- Suffix / Stepper -->
                <div class="tw:absolute tw:right-2 tw:top-1/2 tw:-translate-y-1/2 tw:flex tw:items-center tw:gap-1">
                    <slot name="suffix"></slot>

                    <!-- Stepper Buttons -->
                    <div v-if="showStepper && !readonly && !disabled" class="tw:flex tw:flex-col tw:border tw:border-slate-200 tw:rounded tw:overflow-hidden">
                        <Button
                            :ignore-base-classes="true"
                            custom-class="tw:px-1.5 tw:py-0.5 tw:bg-white hover:tw:bg-slate-50 tw:text-slate-500 tw:transition-colors tw:border-b tw:border-slate-200 tw:flex tw:items-center tw:justify-center"
                            @click="increment"
                        >
                            <svg class="tw:w-3 tw:h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                            </svg>
                        </Button>
                        <Button
                            :ignore-base-classes="true"
                            custom-class="tw:px-1.5 tw:py-0.5 tw:bg-white hover:tw:bg-slate-50 tw:text-slate-500 tw:transition-colors tw:flex tw:items-center tw:justify-center"
                            @click="decrement"
                        >
                            <svg class="tw:w-3 tw:h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </Button>
                    </div>
                </div>
            </div>

            <!-- Error Message -->
            <transition
                enter-active-class="tw:transition tw:duration-200 tw:ease-out"
                enter-from-class="tw:transform tw:-translate-y-1 tw:opacity-0"
                enter-to-class="tw:transform tw:translate-y-0 tw:opacity-100"
                leave-active-class="tw:transition tw:duration-150 tw:ease-in"
                leave-from-class="tw:transform tw:translate-y-0 tw:opacity-100"
                leave-to-class="tw:transform tw:-translate-y-1 tw:opacity-0"
            >
                <div v-if="errors[0] || error" class="tw:relative tw:mb-3">
                    <span class="tw:absolute tw:left-0 tw:top-0 tw:text-xs tw:font-medium tw:text-red-600 tw:whitespace-normal tw:min-w-max tw:max-w-[200%] tw:z-10">
                        {{ errors[0] || error }}
                    </span>
                </div>
            </transition>
        </ValidationProvider>
    </div>
</template>

<script setup>
    import { computed, ref, getCurrentInstance } from 'vue';
    import Button from '@/modules/admin/ui/Button.vue';

    const props = defineProps({
        value: {
            type: [Number, String],
            default: null
        },
        label: {
            type: String,
            default: ''
        },
        id: {
            type: String,
            default: () => `input-number-${getCurrentInstance()?.uid ?? Date.now()}`
        },
        name: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: ''
        },
        vid: {
            type: String,
            default: undefined
        },
        rules: {
            type: [String, Object],
            default: ''
        },
        variant: {
            type: String,
            default: 'outline',
            validator: v => ['outline', 'ghost', 'underlined'].includes(v)
        },
        min: {
            type: Number,
            default: undefined
        },
        max: {
            type: Number,
            default: undefined
        },
        step: {
            type: Number,
            default: 1
        },
        disabled: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        showStepper: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: ''
        },
        customClass: {
            type: String,
            default: ''
        },
        decimalsOnly: {
            type: Boolean,
            default: false
        },
        disallowNegatives: {
            type: Boolean,
            default: false
        },
        decimalRule: {
            type: String,
            default: 'decimal_requerido'
        },
        allowDecimal: {
            type: Boolean,
            default: false
        }
    });

    const emit = defineEmits(['input', 'blur', 'focus']);

    const inputRef = ref(null);
    
    const computedRules = computed(() => {
        const base = props.rules || '';
        if (props.decimalsOnly) {
            return base ? `${base}|${props.decimalRule}` : props.decimalRule;
        }
        return base;
    });
    
    const rootClasses = computed(() => {
        const hasWidthClass = props.customClass && (
            props.customClass.includes('tw:w-') || 
            props.customClass.split(' ').some(c => c.startsWith('w-'))
        );
        return hasWidthClass ? '' : 'tw:w-full';
    });

    const variantClasses = computed(() => {
        switch (props.variant) {
            case 'ghost':
                return 'tw:bg-slate-100 tw:border-transparent focus:tw:bg-white focus:tw:border-blue-400 tw:border tw:p-2 tw:rounded';
            case 'underlined':
                return 'tw:bg-transparent tw:border-b-2 tw:border-slate-300 focus:tw:border-blue-500 tw:px-1 tw:py-2 tw:rounded-t';
            case 'outline':
            default:
                return 'tw:bg-white tw:border tw:border-slate-300 focus:tw:border-blue-500 tw:p-2 tw:rounded';
        }
    });

    const onInput = event => {
        const rawValue = event.target.value;
        if (rawValue === '') {
            emit('input', null);
            return;
        }
        if (props.decimalsOnly) {
            // Normalize locale-ES comma decimal separator to dot, so the
            // value stored in localData is always in canonical dot format
            // regardless of what the user typed (3,1 → 3.1). The display
            // and submit pipeline both expect dots; the validation rule
            // 'decimal_opcional' accepts both.
            emit('input', rawValue.replace(',', '.'));
            return;
        }
        if (props.allowDecimal) {
            const numericValue = parseFloat(rawValue);
            if (!Number.isNaN(numericValue) && Number.isFinite(numericValue)) {
                emit('input', numericValue);
            } else {
                emit('input', null);
            }
            return;
        }
        const numericValue = Number(rawValue);
        if (Number.isFinite(numericValue)) {
            if ((props.max !== undefined && numericValue > props.max) ||
                (props.min !== undefined && numericValue < props.min)) {
                // Revertir al último valor válido en lugar de clampear
                const prev = props.value != null ? String(props.value) : '';
                event.target.value = prev;
                return;
            }
            emit('input', numericValue);
        } else {
            emit('input', null);
        }
    };

    const onKeydown = event => {
        if (props.readonly || props.disabled) return;

        const key = event.key;
        // Allow control keys (backspace, arrows, tab, etc.) — these don't
        // produce printable characters.
        if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End', 'Enter', 'Escape'].includes(key)) return;
        if (event.ctrlKey || event.metaKey || event.altKey) return;

        // decimalsOnly and allowDecimal both render as type="text"
        // (to avoid locale-formatted commas on type="number"), so the
        // browser does NOT block non-numeric keystrokes. Filter them
        // here: allow digits, dot, comma, minus; anything else is blocked.
        if (props.decimalsOnly || props.allowDecimal) {
            // Option opt-out: bloquea el '-' incluso si allowDecimal o
            // decimalsOnly lo permiten por defecto (consumers que sólo
            // admiten no-negativos lo activan explícitamente).
            if (props.disallowNegatives) {
                if (key === '-') {
                    event.preventDefault();
                    return;
                }
            }
            if (!/^[\d.,-]$/.test(key)) {
                event.preventDefault();
                return;
            }
            // Block a second decimal separator
            const input = event.target;
            if ((key === '.' || key === ',') &&
                (input.value || '').indexOf('.') !== -1) {
                event.preventDefault();
                return;
            }
            // Block a second minus
            if (key === '-' && (input.value || '').indexOf('-') !== -1) {
                event.preventDefault();
                return;
            }
            return;
        }

        // Plain numeric input (type="number"): the browser already blocks
        // non-digits, so we only need the min/max guard.
        if (props.max === undefined && props.min === undefined) return;
        if (!/^\d$/.test(key)) return;

        const input = event.target;
        const currentValue = input.value;
        const start = input.selectionStart;
        const end = input.selectionEnd;

        const proposed = currentValue.slice(0, start) + key + currentValue.slice(end);
        const numericValue = Number(proposed);

        if (props.max !== undefined && numericValue > props.max) event.preventDefault();
        if (props.min !== undefined && numericValue < props.min) event.preventDefault();
    };

    const increment = () => {
        if (props.disabled || props.readonly) return;
        const currentVal = Number(props.value || 0);
        const newVal = currentVal + props.step;
        if (props.max !== undefined && newVal > props.max) return;
        emit('input', newVal);
    };

    const decrement = () => {
        if (props.disabled || props.readonly) return;
        const currentVal = Number(props.value || 0);
        const newVal = currentVal - props.step;
        if (props.min !== undefined && newVal < props.min) return;
        emit('input', newVal);
    };
    </script>

<script>
    export default {
        inheritAttrs: false
    };
</script>

<style scoped>
    /* Remove default arrow buttons for number input */
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    input[type=number] {
        -moz-appearance: textfield;
    }
</style>
